import { AiGenerationResult, type AiGenerationResultT, type Job } from "@malfak/contracts";
import { useNavigate } from "@tanstack/react-router";
import { CircleAlert, RotateCcw, Sparkles, SquarePen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Num } from "@/components/ui/misc";
import { useT } from "@/lib/i18n";
import { sendErrorInfo } from "@/lib/reauth";
import { cn, isArabic } from "@/lib/utils";
import { EditImageButton } from "@/features/media-editor";

export const isAiJob = (job: Pick<Job, "kind">) => job.kind.startsWith("ai.");

/** The result an ai.* job kept (operations.result), or null. */
export function aiResultOf(job: Pick<Job, "kind" | "result">): AiGenerationResultT | null {
  if (!isAiJob(job) || !job.result) return null;
  const r = AiGenerationResult.safeParse(job.result);
  return r.success ? r.data : null;
}

/**
 * Preview of what an AI job produced (Jobs center row + drawer, AI Studio history): image thumbnails, caption options,
 * the score or the transcript, with "Use in post" (the composer opens with the image / caption).
 */
export function AiResultPreview({ result, compact, className }: { result: AiGenerationResultT; compact?: boolean; className?: string }) {
  const { t } = useT();
  const navigate = useNavigate();
  const images = result.media.filter((m) => m.mime.startsWith("image/"));
  const audio = result.media.find((m) => m.mime.startsWith("audio/"));
  const caption = result.options?.[0];
  const captionText = caption ? (caption.hashtags.length ? `${caption.caption}\n\n${caption.hashtags.join(" ")}` : caption.caption) : null;
  return (
    <div className={cn("space-y-2", className)} data-testid="ai-result">
      {images.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {images.map((m) => (
            <li key={m.id}>
              <img src={m.url} alt="" className={cn("rounded-xl border border-line object-cover", compact ? "size-16" : "size-28")} data-testid="ai-result-image" />
            </li>
          ))}
        </ul>
      )}
      {captionText && (
        <p className={cn("whitespace-pre-line rounded-xl bg-surface-2 px-3 py-2 text-[12.5px] text-ink", compact && "line-clamp-2")} dir={isArabic(captionText) ? "rtl" : "ltr"}>
          {captionText}
        </p>
      )}
      {result.score && (
        <p className="text-[12.5px] font-medium text-ink">
          <Num>{t("jobs.ai.score", { score: result.score.score })}</Num>
        </p>
      )}
      {result.text && (
        <p className="rounded-xl bg-surface-2 px-3 py-2 text-[12.5px] text-ink" dir="auto">
          {result.text}
        </p>
      )}
      {audio && <audio controls src={audio.url} className="h-8 w-full max-w-xs" aria-label={t("jobs.ai.result")} />}
      {(images[0] || captionText) && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="xs"
            variant="secondary"
            icon={<SquarePen />}
            data-testid="ai-use-in-post"
            onClick={() => void navigate({ to: "/content/compose", search: images[0] ? { mediaId: images[0].id } : { caption: captionText! } })}
          >
            {t("jobs.ai.useInPost")}
          </Button>
          {images[0] && <EditImageButton media={images[0]} size="xs" variant="ghost" testId="ai-result-edit" />}
        </div>
      )}
    </div>
  );
}

/**
 * In-place state of an AI job the page started: "working in the background" while it runs (aria-busy), or the typed
 * failure reason with Retry (same input). Renders nothing when idle or done.
 */
export function AiJobInline({ job, runningText, className }: { job: { isPending: boolean; failed: { errorCode: string | null; error: string | null } | null; retry: () => Promise<void> }; runningText?: string; className?: string }) {
  const { t } = useT();
  if (job.isPending) {
    return (
      <p className={cn("flex items-center gap-2 rounded-xl bg-surface-2 px-3 py-2 text-[12.5px] text-ink-2", className)} aria-busy="true" aria-live="polite" data-testid="ai-job-running">
        <Sparkles className="size-3.5 shrink-0 text-accent motion-safe:animate-pulse" aria-hidden />
        {runningText ?? t("studio.job.running")}
      </p>
    );
  }
  if (!job.failed) return null;
  const info = sendErrorInfo(job.failed);
  return (
    <div role="alert" className={cn("flex flex-wrap items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2 text-[12.5px]", className)} data-testid="ai-job-failed" data-error-code={info.code}>
      <CircleAlert className="size-4 shrink-0 text-bad" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="font-semibold text-ink">{t("studio.job.failed")}</span> <span className="text-ink-2">{info.message}</span>
      </span>
      <Button size="xs" variant="secondary" icon={<RotateCcw />} onClick={() => void job.retry()} data-testid="ai-job-retry">
        {t("studio.job.retry")}
      </Button>
    </div>
  );
}

/** Jobs center: the result block of a finished ai.* job (nothing for other kinds). */
export function AiJobResult({ job, compact }: { job: Job; compact?: boolean }) {
  const { t } = useT();
  if (!isAiJob(job) || job.status !== "succeeded") return null;
  const r = aiResultOf(job);
  if (!r) return compact ? null : <p className="text-[12px] text-ink-3">{t("jobs.ai.noResult")}</p>;
  return <AiResultPreview result={r} compact={compact} className="mt-2" />;
}
